import { Utilization } from "./marketing-client-utilization";
import { DeviceInformationHelper, DeviceType } from "./helpers/device-information-helper";
import { BigDataEventConstants } from "./constants/big-data-constants";
import { EventType } from "./constants/event-type-constants";
import { MarketingClientConstants } from "./constants/marketing-client-constants";
import { DOMHelper } from "./helpers/dom-helper";
import { PageInformationHelper } from "./helpers/page-information-helper";
import { httpInstance } from "./http/http";
import { BigData } from "./marketing-client-bigdata";
import { Spinner } from "./utils/spinner";
import { CtaObject, Utils } from "./utils/utils";
import { LayerType, ShareArticleErrorHandler, WYBORCZA_PL } from "./helpers/swf-helper";

export abstract class MarketingClientSWFBase {
  layers = {};
  mkdmContainerClass = ".mkdm-container";
  loggedHideClass = ".logged-hide";
  form: HTMLFormElement | null;
  email: HTMLInputElement | null;
  spinner: Spinner;
  errorHandler: ShareArticleErrorHandler;
  isMobile: Boolean;
  shareArticleFormId = "shareArticleForm";
  emailClass = ".share-article-email";
  spinnerClass = ".share-article-spinner";
  messages = {
    required: "Pole e-mail jest wymagane"
  };
  manualUtilizeAttr = "data-utilize-manual";
  baseURL = "/share/sender/sendEmail";

  constructor(public utilizationObject: Utilization) {
    this.isMobile = DeviceInformationHelper.getDeviceType() === DeviceType.MOBILE;
  }

  initialize() {
    this.form = DOMHelper.getElementById(this.shareArticleFormId) as HTMLFormElement;

    if (!this.form) {
      return;
    }

    this.email = this.form ? (DOMHelper.querySelector(this.emailClass, this.form) as HTMLInputElement) : null;
    this.spinner = new Spinner(this.spinnerClass);
    this.errorHandler = new ShareArticleErrorHandler(this.email);
    this.initializeLayers();
    this.hideContainersForLoggedInUser();
  }

  initializeLayers() {
    this.layers[LayerType.DEFAULT] = DOMHelper.getElementById("shareArticleDefaultLayer");
    this.layers[LayerType.ERROR] = DOMHelper.getElementById("shareArticleErrorLayer");
    this.layers[LayerType.SUCCESS] = DOMHelper.getElementById("shareArticleSuccessLayer");
    this.layers[LayerType.BLOCKED] = DOMHelper.getElementById("shareArticleBlockedLayer");
  }

  hideContainersForLoggedInUser() {
    if (WYBORCZA_PL?.userInfo?.loggedIn) {
      const mkdmContainer = DOMHelper.querySelector(this.mkdmContainerClass),
        mkdmContainerLoggedHide = mkdmContainer && DOMHelper.querySelector(this.loggedHideClass, mkdmContainer);

      if (mkdmContainer && mkdmContainerLoggedHide) {
        mkdmContainerLoggedHide.classList.add(MarketingClientConstants.hiddenClassName);
      }
    }
  }

  bindEventsToFormSubmit() {
    if (this.form) {
      this.form.addEventListener(EventType.SUBMIT, (e) => {
        e.preventDefault();

        if (this.validateEmail()) {
          this.utilizeShareArticle();
          this.sendData();
        }
      });
    }
  }

  bindEventsToEmailInput() {
    if (this.email) {
      this.email.addEventListener(EventType.CHANGE, this.validateEmail.bind(this));
      this.email.addEventListener(EventType.INPUT, () => {
        return this.email?.value.length ? this.errorHandler.hide() : this.errorHandler.show(this.messages.required);
      });
    }
  }

  validateEmail() {
    const value = this.email ? this.email.value : "",
      emailIsValid = Utils.validateEmail(value);

    if (!value.length) {
      return this.errorHandler.show(this.messages.required);
    } else if (!emailIsValid) {
      return this.errorHandler.show();
    } else {
      return this.errorHandler.hide();
    }
  }

  utilizeShareArticle() {
    const link: Element | null = DOMHelper.querySelector(`[${this.manualUtilizeAttr}]`, this.form as Element),
      parent: Element | null | undefined =
        link && link.closest(`[${MarketingClientConstants.placeholderDataSetAttribute}]`),
      condition: string | null | undefined = link && link.getAttribute(this.manualUtilizeAttr);

    if (link && parent && condition) {
      const dataToSend = this.utilizationObject.prepareDataToSend(parent, condition),
        ctaObject: CtaObject = Utils.prepareCtaObject(parent),
        attribute = BigData.prepareDataOceanAttribute(ctaObject, dataToSend);

      BigData.sendDataOceanEvent(BigDataEventConstants.onClick, attribute);
      this.utilizationObject.utilize(dataToSend);
    }
  }

  sendData() {
    let data = {
      senderEmail: WYBORCZA_PL.userInfo && WYBORCZA_PL.userInfo.email ? WYBORCZA_PL.userInfo.email : "",
      recipientEmail: this.email ? this.email.value : "",
      articleId: Number(PageInformationHelper.getArticleId())
    };

    this.spinner.show();

    return httpInstance
      .post({
        url: this.baseURL,
        data,
        withCredentials: true
      })
      .then(this.successResponseHandler.bind(this), this.errorResponseHandler.bind(this))
      .then(() => {
        this.spinner.hide();
      });
  }

  abstract successResponseHandler(response: any): void;
  abstract errorResponseHandler(): void;
}
